/**
 * Staying signed in.
 *
 * Two very different things can be remembered, and they carry different risk:
 *
 * - **A NIP-46 connection.** What is stored is the *client* key this browser
 *   generated plus the signer's address — an authorisation to ask the phone,
 *   not the key itself. Your nsec never existed here. This is the whole point
 *   of remote signing, so it is remembered by default.
 *
 * - **A local nsec.** That is the key. Storing it makes the archive only as
 *   private as the browser profile around it, so it is opt-in and the UI says
 *   what it means rather than calling it "remember me".
 */
import { bytesToHex, hexToBytes } from "./hex";

// The key keeps the old product name. A new name would sign every browser out.
const KEY = "halimail.session";

export interface StoredRemote {
  kind: "amber";
  /** The throwaway key authorising this browser to ask the signer. */
  clientSecret: string;
  /** Where the signer is: its pubkey, relays and connection secret. */
  pointer: { pubkey: string; relays: string[]; secret: string | null };
}

export interface StoredLocal {
  kind: "local";
  secret: string;
}

export type StoredSession = StoredRemote | StoredLocal;

export function remember(session: StoredSession): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // Private windows and blocked site data both throw. Staying signed in is a
    // convenience; failing to is not worth an error the user cannot act on.
  }
}

export function recall(): StoredSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (parsed.kind === "amber" || parsed.kind === "local") return parsed;
    return null;
  } catch {
    return null;
  }
}

export function forget(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

export { bytesToHex, hexToBytes };
